/**
 * ============================================================
 * FAQ Unified v3.0 - Olimpo Esportes
 * JavaScript para página de Perguntas Frequentes
 * 
 * ✅ Accordion: Expand/collapse com animações
 * ✅ Search: Busca em tempo real
 * ✅ Categories: Navegação por tabs
 * ✅ Performance: Debounce e otimizações
 * ✅ Acessibilidade: ARIA completo
 * ============================================================
 */

(function() {
  'use strict';

  /**
   * Classe principal do FAQ Unificado
   */
  class FAQUnified {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.autoExpand = section.dataset.autoExpand === 'true';
      
      // Elementos principais
      this.searchInput = section.querySelector('.faq-unified__search-input');
      this.searchClear = section.querySelector('.faq-unified__search-clear');
      this.navItems = section.querySelectorAll('.faq-unified__nav-item');
      this.categories = section.querySelectorAll('.faq-unified__category');
      this.faqItems = section.querySelectorAll('.faq-unified__item');
      this.questions = section.querySelectorAll('.faq-unified__question');
      this.noResults = section.querySelector('.faq-unified__no-results');
      this.resetSearch = section.querySelector('.faq-unified__reset-search');
      this.totalCount = section.querySelector('[data-total-count]');
      this.tags = section.querySelectorAll('.faq-unified__tag');
      
      // Estado
      this.currentCategory = 'all';
      this.searchQuery = '';
      this.visibleCount = this.faqItems.length;
      
      this.init();
    }

    /**
     * Inicializa todos os handlers
     */
    init() {
      this.bindAccordion();
      this.bindSearch();
      this.bindNavigation();
      this.bindTags();
      this.bindResetSearch();
      this.updateCategoryCounts();
      
      // Auto-expand primeira pergunta se configurado
      if (this.autoExpand && this.faqItems.length > 0) {
        this.toggleItem(this.faqItems[0], true);
      }
      
      // Suporte a hash na URL
      this.handleUrlHash();
    }

    /**
     * Accordion - Expand/Collapse
     */
    bindAccordion() {
      this.questions.forEach(question => {
        question.addEventListener('click', (e) => {
          const item = question.closest('.faq-unified__item');
          const isActive = item.classList.contains('faq-unified__item--active');
          
          // Fecha todos os outros (comportamento accordion)
          this.closeAllItems();
          
          // Toggle do item clicado
          if (!isActive) {
            this.toggleItem(item, true);
          }
        });

        // Suporte a teclado
        question.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            question.click();
          }
        });
      });
    }

    /**
     * Fecha todos os itens
     */
    closeAllItems() {
      this.faqItems.forEach(item => {
        this.toggleItem(item, false);
      });
    }

    /**
     * Toggle de um item específico
     */
    toggleItem(item, open) {
      const question = item.querySelector('.faq-unified__question');
      
      if (open) {
        item.classList.add('faq-unified__item--active');
        question.setAttribute('aria-expanded', 'true');
      } else {
        item.classList.remove('faq-unified__item--active');
        question.setAttribute('aria-expanded', 'false');
      }
    }

    /**
     * Search - Busca em tempo real
     */
    bindSearch() {
      if (!this.searchInput) return;

      // Debounce para melhor performance
      let debounceTimer;
      
      this.searchInput.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          this.searchQuery = e.target.value.toLowerCase().trim();
          this.filterFAQs();
          this.updateClearButton();
        }, 200);
      });

      // Limpar busca
      if (this.searchClear) {
        this.searchClear.addEventListener('click', () => {
          this.clearSearch();
        });
      }

      // ESC para limpar
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.clearSearch();
        }
      });
    }

    /**
     * Limpa a busca
     */
    clearSearch() {
      if (this.searchInput) {
        this.searchInput.value = '';
        this.searchQuery = '';
        this.filterFAQs();
        this.updateClearButton();
        this.searchInput.focus();
      }
    }

    /**
     * Atualiza visibilidade do botão clear
     */
    updateClearButton() {
      if (this.searchClear) {
        if (this.searchQuery.length > 0) {
          this.searchClear.classList.add('active');
        } else {
          this.searchClear.classList.remove('active');
        }
      }
    }

    /**
     * Navigation - Tabs de categorias
     */
    bindNavigation() {
      this.navItems.forEach(item => {
        item.addEventListener('click', () => {
          const category = item.dataset.category;
          this.setActiveCategory(category);
          this.filterFAQs();
        });
      });
    }

    /**
     * Define categoria ativa
     */
    setActiveCategory(category) {
      this.currentCategory = category;
      
      this.navItems.forEach(item => {
        if (item.dataset.category === category) {
          item.classList.add('faq-unified__nav-item--active');
          item.setAttribute('aria-pressed', 'true');
        } else {
          item.classList.remove('faq-unified__nav-item--active');
          item.setAttribute('aria-pressed', 'false');
        }
      });
    }

    /**
     * Tags de busca rápida
     */
    bindTags() {
      this.tags.forEach(tag => {
        tag.addEventListener('click', () => {
          const searchText = tag.dataset.search;
          if (this.searchInput && searchText) {
            this.searchInput.value = searchText;
            this.searchQuery = searchText.toLowerCase().trim();
            this.filterFAQs();
            this.updateClearButton();
            
            // Scroll para resultados
            this.section.querySelector('.faq-unified__main')?.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      });
    }

    /**
     * Reset search button
     */
    bindResetSearch() {
      if (this.resetSearch) {
        this.resetSearch.addEventListener('click', () => {
          this.clearSearch();
          this.setActiveCategory('all');
          this.filterFAQs();
        });
      }
    }

    /**
     * Filtra FAQs baseado em busca e categoria
     */
    filterFAQs() {
      let visibleCount = 0;
      const visibleCategories = new Set();

      this.faqItems.forEach(item => {
        const itemCategory = item.dataset.category;
        const question = item.dataset.question || '';
        const answer = item.dataset.answer || '';
        
        // Verifica categoria
        const categoryMatch = this.currentCategory === 'all' || itemCategory === this.currentCategory;
        
        // Verifica busca
        let searchMatch = true;
        if (this.searchQuery) {
          searchMatch = question.includes(this.searchQuery) || answer.includes(this.searchQuery);
        }
        
        // Aplica filtro
        if (categoryMatch && searchMatch) {
          item.hidden = false;
          visibleCount++;
          visibleCategories.add(itemCategory);
        } else {
          item.hidden = true;
          // Fecha item se estava aberto
          this.toggleItem(item, false);
        }
      });

      // Mostra/esconde categorias
      this.categories.forEach(category => {
        const categoryHandle = category.dataset.category;
        const hasVisibleItems = visibleCategories.has(categoryHandle);
        
        if (this.currentCategory === 'all') {
          category.hidden = !hasVisibleItems;
        } else {
          category.hidden = categoryHandle !== this.currentCategory;
        }
      });

      // Atualiza contador
      this.visibleCount = visibleCount;
      this.updateResultsCount();
      
      // Mostra/esconde mensagem "sem resultados"
      if (this.noResults) {
        if (visibleCount === 0) {
          this.noResults.hidden = false;
        } else {
          this.noResults.hidden = true;
        }
      }
    }

    /**
     * Atualiza contadores nas tabs
     */
    updateCategoryCounts() {
      const counts = {};
      
      // Conta FAQs por categoria
      this.faqItems.forEach(item => {
        const category = item.dataset.category;
        if (category) {
          counts[category] = (counts[category] || 0) + 1;
        }
      });
      
      // Atualiza elementos de contagem
      this.navItems.forEach(item => {
        const category = item.dataset.category;
        const countEl = item.querySelector('.faq-unified__nav-count');
        
        if (countEl && category !== 'all') {
          countEl.textContent = counts[category] || 0;
        }
      });
    }

    /**
     * Atualiza contador de resultados
     */
    updateResultsCount() {
      if (this.totalCount) {
        this.totalCount.textContent = this.visibleCount;
        
        // Atualiza texto singular/plural
        const parent = this.totalCount.closest('.faq-unified__results-count');
        if (parent) {
          const text = this.visibleCount === 1 
            ? `<strong>${this.visibleCount}</strong> pergunta encontrada`
            : `<strong>${this.visibleCount}</strong> perguntas encontradas`;
          parent.innerHTML = text;
        }
      }
    }

    /**
     * Suporte a hash na URL
     */
    handleUrlHash() {
      const hash = window.location.hash.slice(1);
      if (hash) {
        // Tenta encontrar categoria
        const matchingNav = Array.from(this.navItems).find(
          item => item.dataset.category === hash
        );
        
        if (matchingNav) {
          this.setActiveCategory(hash);
          this.filterFAQs();
        }
      }
    }
  }

  /**
   * Inicializa quando DOM estiver pronto
   */
  function init() {
    const sections = document.querySelectorAll('.faq-unified');
    sections.forEach(section => new FAQUnified(section));
  }

  // Inicializa
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Suporte ao Theme Editor
  if (Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector('.faq-unified');
      if (section) new FAQUnified(section);
    });
  }
})();

